import { useState } from "react";
import { ABOUT_PAGES, WORKFLOW_STAGES } from "@/lib/aboutPages";
import { ChevronDown, ChevronUp, Info, HelpCircle, Compass, Search, ListChecks, ArrowRightCircle, ArrowRight } from "lucide-react";

const COLUMNS = [
  ["What is this page?", "what", Info],
  ["Why is it important?", "why", HelpCircle],
  ["When should I use it?", "when", Compass],
  ["How should I interpret the results?", "interpret", Search],
  ["What should I do next?", "next", ArrowRightCircle],
];

export const AboutPagePanel = ({ view }) => {
  const cfg = ABOUT_PAGES[view];
  const storageKey = `about_panel_${view}`;
  const [open, setOpen] = useState(() => localStorage.getItem(storageKey) === "open");

  if (!cfg) return null;

  const toggle = () => {
    const next = !open;
    setOpen(next);
    localStorage.setItem(storageKey, next ? "open" : "closed");
  };

  return (
    <div className="bg-sky-50/40 border border-sky-200/70 rounded-xl mb-5" data-testid={`about-panel-${view}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left" data-testid={`about-toggle-${view}`}>
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Info className="w-4 h-4 text-teal-600 shrink-0" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-slate-900 font-semibold shrink-0">
            About this page · <span className="text-teal-700">{cfg.name}</span>
          </span>
          {!open && <span className="text-[11px] text-slate-500 truncate hidden md:inline font-normal normal-case tracking-normal font-sans">— {cfg.summary}</span>}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden xl:flex items-center" data-testid={`workflow-strip-${view}`}>
            {WORKFLOW_STAGES.map((s, i) => (
              <span key={s} className="flex items-center">
                <span className={`text-[9px] font-mono uppercase tracking-wide rounded-full px-2 py-0.5 border ${s === cfg.stage ? "bg-teal-600 text-white border-teal-600 font-semibold" : "text-slate-400 border-slate-200 bg-white"}`}>{s}</span>
                {i < WORKFLOW_STAGES.length - 1 && <ArrowRight className="w-2.5 h-2.5 text-slate-300 mx-1" />}
              </span>
            ))}
          </div>
          {open ? <ChevronUp className="w-4 h-4 text-slate-500" data-testid={`about-chevron-up-${view}`} /> : <ChevronDown className="w-4 h-4 text-slate-500" data-testid={`about-chevron-down-${view}`} />}
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4" data-testid={`about-content-${view}`}>
          <p className="text-xs text-slate-600 leading-snug mb-3 max-w-4xl">{cfg.summary}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {COLUMNS.map(([title, key, Icon]) => (
              <div key={key} className="bg-white border border-sky-100 rounded-lg p-3">
                <p className="text-[11px] font-bold text-slate-900 flex items-center gap-1.5 mb-1"><Icon className="w-3.5 h-3.5 text-teal-600 shrink-0" />{title}</p>
                <p className="text-[11px] text-slate-600 leading-snug">{cfg[key]}</p>
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-3">
            {[["What this module analyses", cfg.analyses], ["Questions it answers", cfg.questions], ["Expected actions", cfg.actions]].map(([title, items]) => (
              <div key={title} className="border border-sky-100/80 rounded-lg p-2.5 bg-white/60">
                <p className="text-[10px] font-mono uppercase tracking-widest text-teal-700 flex items-center gap-1 mb-1"><ListChecks className="w-3 h-3" />{title}</p>
                <ul className="space-y-0.5">
                  {items.map((it) => <li key={it} className="text-[10.5px] text-slate-600 leading-snug flex items-start gap-1.5"><span className="w-1 h-1 rounded-full bg-teal-400 mt-1.5 shrink-0" />{it}</li>)}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
